/**
 * Calendario de vencimientos: un mes a la vista, con lo que vence cada día.
 *
 * El pedido original del dueño era «vencimientos de las cosas que vendemos»
 * —`sales.subscription.end_date`, que en pantalla se llama «Llamar el»—. El
 * pedido nuevo lo amplía y es literal: **«todo lo que tenga fecha de
 * vencimiento»**, o sea las ventas, los reclamos con plazo y los
 * seguimientos/pendientes agendados, en el mismo mes y distinguibles de un
 * vistazo.
 *
 * DOS FUENTES DE DATOS, UN SOLO CAMINO DE DIBUJO
 * ===============================================
 * Esta pantalla sabe leer de dos lados, y es lo que le permite servir para las
 * dos cosas sin partirse en dos componentes:
 *
 * 1. **La agenda unificada**, cuando la acción del menú declara
 *    `context.calendar_agenda_url`. El servidor devuelve las tres fuentes ya
 *    mezcladas y con UNA MISMA FORMA (`ItemDeAgenda`), así que acá no hay que
 *    saber qué es un reclamo ni cómo se llama el campo de fecha de cada uno.
 * 2. **Un solo modelo**, cuando no la declara: `context.calendar_field` y un
 *    `searchRead` contra el spec, que es exactamente lo que esta pantalla hacía
 *    antes y lo que sigue haciendo en un despliegue sin el vertical instalado.
 *
 * Lo importante es que el segundo camino **normaliza a la misma forma que el
 * primero** (ver `itemsDelModelo`). Así el mes, la celda, el pie, el total y el
 * aviso de tope están escritos una sola vez: la diferencia entre los dos modos
 * es de dónde salen los items, y termina ahí.
 *
 * QUÉ SABE Y QUÉ NO SABE ESTA VISTA
 * ==================================
 * No sabe qué es una venta, ni un reclamo, ni un pendiente. Recibe `tipo` (para
 * el color y el icono), `titulo`, `detalle`, `importe` y `apagado` **ya
 * resueltos por el servidor**, incluido el semáforo: antes esta vista comparaba
 * `state` contra los códigos `renewed` y `cancelled`, que era vocabulario de
 * ventas escrito adentro de un renderer genérico, y no había forma de extenderlo
 * a los códigos de otros dos modelos sin acumularlos todos acá.
 *
 * Lo único que este lado decide es el color de cada tipo (`ESTILO_DE_TIPO`), que
 * no es un dato que un backend pueda dar razonablemente. Y hay un estilo por
 * defecto: un tipo desconocido se dibuja igual en vez de romper la pantalla.
 *
 * NO DUPLICA «PRÓXIMOS A VENCER» DEL INICIO
 * ==========================================
 * Son dos preguntas distintas y por eso conviven:
 *
 * * `ProximosPanel` contesta **«¿a quién llamo ahora?»**: una lista corta de lo
 *   vigente que ya venció o vence en 7/30/60 días, ordenada por urgencia, con el
 *   teléfono y el mail a un toque y el botón «Ya lo contacté».
 * * Esto contesta **«¿cómo viene agosto?»**: el mes entero, incluido lo que vence
 *   dentro de tres semanas y lo que venció el mes pasado, repartido por día para
 *   ver dónde se amontona el trabajo.
 *
 * Por eso el pie de cada mes enlaza a la pantalla hermana en vez de repetir sus
 * botones: llamar se hace allá, planificar acá.
 *
 * LO QUE ESTA PANTALLA NO HACE
 * ============================
 * No emite, no factura, no manda un mail y no programa un aviso. Es una agenda
 * que se lee (ARQUITECTURA.md §9 y la regla 1 del proyecto: emitir es siempre un
 * click humano). Lo único que se puede hacer desde acá es ENTRAR al registro.
 *
 * Las fechas se comparan como texto `YYYY-MM-DD` y nunca como `Date`. El motivo
 * está explicado entero en `views/calendario.ts`, que es donde vive el armado de
 * la grilla y donde están los tests.
 */

import clsx from 'clsx';
import { useMemo, type SVGProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { formatCurrency, isMany2One } from '@/components/fields/format';
import {
  IconAlert,
  IconArrowRight,
  IconCalendar,
  IconCheckSquare,
  IconChevronRight,
  IconLifeBuoy,
  IconSpinner,
  IconWallet,
} from '@/components/shell/icons';
import { modelPath, recordPath } from '@/components/shell/menu-utils';
import { Card, ErrorState, PageHeader } from '@/components/shell/primitives';
import { api, ApiError } from '@/lib/api';
import type { Domain, ModelSpec, RecordData } from '@/lib/types';
import { agendaDelRango, esAgendaValida, type ItemDeAgenda } from '@/views/agenda/api';
import {
  armarGrilla,
  correrMes,
  DIAS_DE_LA_SEMANA,
  hoyIso,
  mesActual,
  mesDeLaFecha,
  nombreDelMes,
  rangoDelMes,
  sumarImportes,
  type MesDelCalendario,
} from '@/views/calendario';
import { useCompanyId } from '@/views/queries';

/**
 * Tope de filas del mes en el modo de UN SOLO MODELO.
 *
 * Alto a propósito: el calendario no pagina —un mes al que le falta la mitad de
 * los vencimientos es peor que no tenerlo—. Si alguna vez se pasa, el aviso del
 * pie lo dice en vez de mentir por omisión.
 *
 * En el modo unificado el tope lo pone el servidor, POR FUENTE, y viaja en la
 * respuesta: ver `RespuestaDeAgenda.truncado`.
 */
const TOPE = 500;

/** El campo de fecha por defecto, si la acción no declara ninguno. */
const CAMPO_POR_DEFECTO = 'end_date';

/** El `tipo` que se le pone a lo que sale del modo de un solo modelo. */
const TIPO_GENERICO = 'registro';

/**
 * Lo que la celda de un día sabe dibujar en el modo de UN SOLO MODELO.
 *
 * Se piden **sólo los que el modelo declara**: la vista es genérica y un modelo
 * sin `amount` no tiene por qué recibir un 400 por un campo que esta pantalla
 * pidió de más. Lo que falte, la celda no lo dibuja.
 */
const CAMPOS_DE_LA_CELDA = ['partner', 'name', 'amount', 'state'] as const;

/**
 * El color y el icono de cada tipo. **Es lo único del oficio que vive acá**, y
 * es lo único que tiene que vivir acá: un backend no tiene por qué opinar sobre
 * el color de un borde.
 *
 * `defecto` no es un descuido: un tipo que este mapa no conozca —una cuarta
 * fuente que el servidor agregue mañana— se dibuja con el estilo neutro y
 * aparece en el mes igual. La alternativa (romper, o no dibujarlo) haría que
 * agregar una fuente del lado del servidor exija tocar React el mismo día.
 */
const ESTILO_DE_TIPO: Record<
  string,
  { borde: string; texto: string; Icono: (props: SVGProps<SVGSVGElement>) => JSX.Element }
> = {
  venta: { borde: 'border-l-accent', texto: 'text-accent', Icono: IconWallet },
  reclamo: { borde: 'border-l-warning', texto: 'text-warning', Icono: IconLifeBuoy },
  pendiente: { borde: 'border-l-info', texto: 'text-info', Icono: IconCheckSquare },
};

const ESTILO_POR_DEFECTO = {
  borde: 'border-l-accent',
  texto: 'text-fg-muted',
  Icono: IconCalendar,
};

function estiloDe(tipo: string): (typeof ESTILO_DE_TIPO)[string] {
  return ESTILO_DE_TIPO[tipo] ?? ESTILO_POR_DEFECTO;
}

export interface CalendarViewProps {
  spec: ModelSpec;
  /** Campo de fecha sobre el que se arma el mes (`context.calendar_field`). */
  dateField: string;
  /** Título de la pantalla: el rótulo del menú por el que se entró. */
  title: string;
  /** Filtro base de la acción del menú, si tiene. Se aplica siempre. */
  baseDomain?: Domain;
  /** Menú de origen, para que las migas y los enlaces digan la rama real. */
  menuName?: string | null;
  /**
   * Ruta del endpoint que devuelve **todo lo que vence**, declarada por el
   * módulo dueño de ese endpoint en `context.calendar_agenda_url`.
   *
   * Con ella, el mes se arma con las tres fuentes; sin ella, con el modelo del
   * spec y nada más. La ruta viaja declarada —y no escrita acá— por lo mismo que
   * el rótulo de la pantalla hermana: esta vista es genérica y no tiene por qué
   * saber que existe un módulo llamado «consorcios».
   */
  agendaUrl?: string;
  /**
   * Pantalla hermana que se enlaza al pie, declarada por la acción
   * (`context.calendar_sibling_menu` / `calendar_sibling_label`).
   *
   * Existe para que el vocabulario del oficio —«A quién llamar»— NO quede
   * escrito en este componente, que es genérico. Sin las dos, el pie no se
   * dibuja y la pantalla funciona igual.
   */
  siblingMenu?: string;
  siblingLabel?: string;
}

/** El mes que dice la URL (`?mes=2026-08`), o el de hoy. */
function mesDeLaUrl(crudo: string | null): MesDelCalendario {
  if (crudo === null) return mesActual();
  const partes = /^(\d{4})-(\d{2})$/.exec(crudo);
  if (partes === null) return mesActual();
  const mes = Number(partes[2]);
  if (mes < 1 || mes > 12) return mesActual();
  return { anio: Number(partes[1]), mes };
}

function claveDeMes(mes: MesDelCalendario): string {
  return `${String(mes.anio).padStart(4, '0')}-${String(mes.mes).padStart(2, '0')}`;
}

function textoDe(valor: unknown): string {
  return typeof valor === 'string' ? valor : '';
}

/**
 * Convierte las filas de UN modelo a la misma forma que devuelve la agenda.
 *
 * Es el puente que permite que los dos modos compartan todo lo demás. Acá —y
 * sólo acá— siguen viviendo los dos códigos de estado de ventas (`renewed` y
 * `cancelled`) que antes estaban en la celda: en el modo unificado los resuelve
 * el servidor, y este modo es justamente el que corre cuando ese servidor no
 * está. Si el modelo no tiene `state`, no aplica y no rompe nada.
 */
function itemsDelModelo(
  filas: readonly RecordData[],
  spec: ModelSpec,
  campo: string,
): ItemDeAgenda[] {
  return filas.map((fila) => {
    const cliente = isMany2One(fila.partner) ? fila.partner : null;
    const estado = textoDe(fila.state);
    const titulo = cliente?.display_name ?? textoDe(fila.name);
    return {
      tipo: TIPO_GENERICO,
      tipo_label: spec.label,
      clave: `${TIPO_GENERICO}-${String(fila.id)}`,
      fecha: textoDe(fila[campo]),
      titulo,
      // Vacío cuando diría lo mismo que el título, que es lo que pasa en cuanto
      // el modelo no tiene cliente: la celda mostraba el nombre dos veces, una
      // debajo de la otra.
      detalle: textoDe(fila.name) === titulo ? '' : textoDe(fila.name),
      importe: spec.fields.amount === undefined ? null : textoDe(fila.amount),
      modelo: spec.model,
      id: Number(fila.id),
      estado,
      estado_label: estado,
      apagado: estado === 'renewed' || estado === 'cancelled',
    };
  });
}

export function CalendarView({
  spec,
  dateField,
  title,
  baseDomain,
  menuName,
  agendaUrl,
  siblingMenu,
  siblingLabel,
}: CalendarViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = useCompanyId();
  const campo = dateField === '' ? CAMPO_POR_DEFECTO : dateField;
  const mes = mesDeLaUrl(searchParams.get('mes'));
  const hoy = hoyIso();
  const mesDeHoy = mesDeLaFecha(hoy);

  const rango = rangoDelMes(mes);
  const unificado = agendaUrl !== undefined && agendaUrl !== '';

  // -- Modo unificado: las tres fuentes, ya mezcladas por el servidor --------
  const consultaAgenda = useQuery({
    // La EMPRESA ACTIVA entra en la clave por el mismo motivo que abajo.
    queryKey: ['calendario-agenda', companyId, agendaUrl, rango.desde, rango.hasta],
    queryFn: ({ signal }) => agendaDelRango(agendaUrl ?? '', rango.desde, rango.hasta, signal),
    enabled: unificado,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  // -- Modo de un solo modelo: lo que esta pantalla hacía antes --------------
  const campos = [
    campo,
    ...CAMPOS_DE_LA_CELDA.filter((nombre) => spec.fields[nombre] !== undefined),
  ];

  // El dominio pide el mes EXACTO, no la grilla completa: los días de relleno
  // son del mes de al lado y se ven en su propia página. Traerlos duplicaría
  // filas entre dos meses y el total del pie dejaría de cerrar con lo que se ve.
  const dominio: Domain = [
    ...(baseDomain ?? []),
    [campo, '>=', rango.desde],
    [campo, '<=', rango.hasta],
  ];

  const consultaModelo = useQuery({
    // La EMPRESA ACTIVA entra en la clave. La consulta viaja siempre con la
    // cabecera `X-Company-Id`, así que cambiar de empresa cambia la respuesta;
    // sin la empresa en la clave, el mes seguiría mostrando lo de la anterior
    // hasta que caduque la caché. Mismo criterio que `views/queries.ts`.
    queryKey: [
      'calendario',
      companyId,
      spec.model,
      campo,
      rango.desde,
      rango.hasta,
      JSON.stringify(baseDomain ?? []),
    ],
    queryFn: ({ signal }) =>
      api.searchRead(
        spec.model,
        {
          domain: dominio,
          order: campo,
          limit: TOPE,
          // `amount` viaja como STRING desde el backend a propósito y NO se pasa
          // por `Number()` en ningún lado de este archivo: se suma con enteros
          // de centavos (ver `sumarImportes` en `views/calendario.ts`).
          fields: campos,
        },
        signal,
      ),
    enabled: !unificado,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const consulta = unificado ? consultaAgenda : consultaModelo;

  /**
   * Los items del mes, vengan de donde vengan.
   *
   * Cuando la respuesta unificada no tiene la forma esperada se cae a la lista
   * vacía en vez de romper: el servidor falso del archivo suelto contesta 200 a
   * toda ruta que no tenga grabada, así que «llegó sin error» no alcanza (ver
   * `esAgendaValida`).
   */
  const items = useMemo<ItemDeAgenda[]>(() => {
    if (unificado) {
      const datos = consultaAgenda.data;
      return esAgendaValida(datos) ? datos.items : [];
    }
    return itemsDelModelo(consultaModelo.data?.results ?? [], spec, campo);
  }, [unificado, consultaAgenda.data, consultaModelo.data, spec, campo]);

  const grilla = useMemo(
    () => armarGrilla<ItemDeAgenda>(mes, items, (item) => item.fecha),
    [mes, items],
  );

  function irAlMes(destino: MesDelCalendario): void {
    setSearchParams((actual) => {
      const copia = new URLSearchParams(actual);
      copia.set('mes', claveDeMes(destino));
      return copia;
    });
  }

  // El total del pie, sumado con enteros de centavos y NUNCA con `Number()`:
  // los importes viajan como texto a propósito (regla 4 del proyecto). Lo que no
  // tiene importe —un reclamo, un pendiente— no suma ni resta.
  const total = useMemo(
    () => sumarImportes(items.map((item) => item.importe ?? '')),
    [items],
  );
  const enElMes = items.length;

  /** Cuántos hay de cada tipo, en el orden en que aparecen. Para el resumen. */
  const porTipo = useMemo(() => {
    const cuenta = new Map<string, { etiqueta: string; cuantos: number }>();
    for (const item of items) {
      const previo = cuenta.get(item.tipo);
      if (previo === undefined) cuenta.set(item.tipo, { etiqueta: item.tipo_label, cuantos: 1 });
      else previo.cuantos += 1;
    }
    return [...cuenta.entries()].map(([tipo, datos]) => ({ tipo, ...datos }));
  }, [items]);

  /**
   * Qué fuentes quedaron cortadas por el tope. Fuente por fuente y no un solo
   * booleano: «faltan ventas» y «faltan pendientes» se arreglan en pantallas
   * distintas, así que el aviso tiene que poder decir cuál falta.
   */
  const truncadas = useMemo(() => {
    if (!unificado) return enElMes >= TOPE ? [spec.label_plural] : [];
    const datos = consultaAgenda.data;
    if (!esAgendaValida(datos)) return [];
    const etiquetas = new Map(datos.tipos.map((t) => [t.valor, t.etiqueta]));
    return Object.entries(datos.truncado)
      .filter(([, corto]) => corto)
      .map(([tipo]) => etiquetas.get(tipo) ?? tipo);
  }, [unificado, consultaAgenda.data, enElMes, spec.label_plural]);

  /** Fuentes que el usuario no puede leer. No es lo mismo que «no hay nada». */
  const sinPermiso = useMemo(() => {
    if (!unificado) return [];
    const datos = consultaAgenda.data;
    if (!esAgendaValida(datos)) return [];
    const etiquetas = new Map(datos.tipos.map((t) => [t.valor, t.etiqueta]));
    return datos.sin_permiso.map((tipo) => etiquetas.get(tipo) ?? tipo);
  }, [unificado, consultaAgenda.data]);

  return (
    <div className="mx-auto w-full max-w-6xl">
      <PageHeader
        title={title}
        description={
          unificado
            ? 'Todo lo que vence cada día del mes: lo vendido, los reclamos con plazo y los pendientes agendados. Un click abre el registro. Acá no se emite ni se avisa nada: es una agenda que se lee.'
            : 'Lo que vence cada día del mes. Un click abre el registro. Acá no se emite ni se avisa nada: es una agenda que se lee.'
        }
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              irAlMes(correrMes(mes, -1));
            }}
            aria-label={`Ir a ${nombreDelMes(correrMes(mes, -1))}`}
            className="grid h-9 w-9 place-items-center rounded-md border border-line text-fg-muted transition-colors hover:text-fg"
          >
            <IconChevronRight className="h-4 w-4 rotate-180" />
          </button>
          {/* `first-letter:uppercase` y no `capitalize`: `capitalize` pone en
              mayúscula CADA palabra y el rótulo salía «Agosto De 2026». El mes
              viene en minúscula del helper justamente para poder componerlo
              dentro de una frase (los `aria-label` de las flechas lo hacen). */}
          <h2 className="min-w-[11rem] px-2 text-center text-lg font-semibold first-letter:uppercase">
            {nombreDelMes(mes)}
          </h2>
          <button
            type="button"
            onClick={() => {
              irAlMes(correrMes(mes, 1));
            }}
            aria-label={`Ir a ${nombreDelMes(correrMes(mes, 1))}`}
            className="grid h-9 w-9 place-items-center rounded-md border border-line text-fg-muted transition-colors hover:text-fg"
          >
            <IconChevronRight className="h-4 w-4" />
          </button>
          {mesDeHoy !== null && (mesDeHoy.anio !== mes.anio || mesDeHoy.mes !== mes.mes) && (
            <button
              type="button"
              onClick={() => {
                irAlMes(mesDeHoy);
              }}
              className="ml-2 rounded-md border border-line px-3 py-1.5 text-sm text-fg-muted transition-colors hover:text-fg"
            >
              Hoy
            </button>
          )}
          {consulta.isFetching && <IconSpinner className="ml-2 h-4 w-4 text-fg-faint" />}
        </div>

        <p className="text-sm text-fg-muted">
          {enElMes === 0
            ? 'Sin vencimientos este mes'
            : `${enElMes} ${enElMes === 1 ? 'vencimiento' : 'vencimientos'} · ${formatCurrency(total, { currency: 'ARS', minimumFractionDigits: 2 })}`}
        </p>
      </div>

      {/* El desglose por tipo: es lo que contesta «de qué es este mes» sin
          contar cuadraditos. Sólo cuando hay más de un tipo — con uno solo
          repetiría el número de arriba. */}
      {porTipo.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-fg-muted">
          {porTipo.map(({ tipo, etiqueta, cuantos }) => {
            const { texto, Icono } = estiloDe(tipo);
            return (
              <span key={tipo} className="inline-flex items-center gap-1.5">
                <Icono className={clsx('h-4 w-4', texto)} aria-hidden="true" />
                <span className="tabular-nums">{cuantos}</span>
                <span>{etiqueta.toLowerCase()}</span>
              </span>
            );
          })}
        </div>
      )}

      {consulta.isError ? (
        <ErrorState
          title="No se pudo cargar el calendario"
          message={
            consulta.error instanceof ApiError
              ? consulta.error.message
              : 'Reintentá en un momento.'
          }
          onRetry={() => {
            void consulta.refetch();
          }}
        />
      ) : consulta.isPending ? (
        <div className="flex items-center justify-center gap-3 py-24 text-fg-muted">
          <IconSpinner className="h-5 w-5" />
          <span className="text-sm">Armando el mes…</span>
        </div>
      ) : (
        <Card className="overflow-hidden">
          {/* El mes SCROLLEA HORIZONTALMENTE dentro de su tarjeta en vez de
              apretar las columnas. Siete columnas en una pantalla de teléfono
              dan 50 px por día: el importe no entra y el nombre del cliente se
              corta en la segunda letra, o sea un calendario que no se puede
              leer. Con el ancho mínimo, el mes se corre con el dedo y la página
              nunca scrollea de costado. */}
          <div className="overflow-x-auto">
            <div className="min-w-[44rem]">
              <div className="grid grid-cols-7 border-b border-line bg-surface-raised/40">
                {DIAS_DE_LA_SEMANA.map((dia) => (
                  <div
                    key={dia}
                    className="px-2 py-2 text-center text-2xs font-semibold uppercase tracking-wide text-fg-faint"
                  >
                    {dia}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {grilla.flat().map((dia) => {
                  const esHoy = dia.fecha === hoy;
                  return (
                    <div
                      key={dia.fecha}
                      className={clsx(
                        'min-h-[7rem] border-b border-r border-line p-1.5 last:border-r-0',
                        !dia.delMes && 'bg-app-subtle/60',
                      )}
                    >
                      <div className="mb-1 flex items-center justify-between px-1">
                        <span
                          className={clsx(
                            'text-xs tabular-nums',
                            esHoy
                              ? 'grid h-5 w-5 place-items-center rounded-full bg-accent font-semibold text-accent-contrast'
                              : dia.delMes
                                ? 'text-fg-muted'
                                : 'text-fg-faint',
                          )}
                        >
                          {dia.numero}
                        </span>
                        {dia.items.length > 1 && (
                          <span className="text-2xs tabular-nums text-fg-faint">
                            {dia.items.length}
                          </span>
                        )}
                      </div>

                      <ul className="space-y-1">
                        {dia.items.map((item) => (
                          <li key={item.clave}>
                            <CeldaDeVencimiento
                              item={item}
                              fecha={dia.fecha}
                              hoy={hoy}
                              menuName={menuName ?? null}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* El aviso de tope, POR FUENTE. Antes era uno solo y sólo podía existir
          en el modo de un modelo; con tres fuentes, un aviso genérico no dice
          qué falta ni dónde buscarlo. */}
      {truncadas.length > 0 && (
        <p className="mt-3 flex items-start gap-2 text-sm text-warning">
          <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
          Este mes tiene más de lo que entra en la pantalla y falta parte de:{' '}
          {truncadas.join(', ')}. Es el único caso en el que el calendario no dice todo: para verlo
          completo, usá la lista con el filtro de fechas.
        </p>
      )}

      {/* No es lo mismo «no hay nada» que «vos no ves esto». Sin este aviso,
          alguien sin permiso sobre una fuente ve un mes corto y concluye que el
          dato no está cargado. */}
      {sinPermiso.length > 0 && (
        <p className="mt-3 flex items-start gap-2 text-sm text-fg-muted">
          <IconAlert className="mt-0.5 h-4 w-4 shrink-0 text-fg-faint" />
          No se muestra {sinPermiso.join(' ni ')} porque tu usuario no tiene permiso para verlo. El
          resto del mes está completo.
        </p>
      )}

      {/* El puente a la pantalla hermana, para que se lea que son distintas y no
          que una está de más. El rótulo lo declara la acción del menú: acá no se
          escribe vocabulario del oficio. */}
      {siblingMenu !== undefined && siblingMenu !== '' && (
        <p className="mt-4 flex flex-wrap items-center gap-1.5 text-sm text-fg-muted">
          <IconCalendar className="h-4 w-4 text-fg-faint" aria-hidden="true" />
          Esto es el mes entero. Para trabajar la lista del día —con el teléfono a mano—, está
          <Link
            to={modelPath(spec.model, siblingMenu)}
            className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
          >
            «{siblingLabel ?? 'la otra pantalla'}»
            <IconArrowRight className="h-3.5 w-3.5" />
          </Link>
        </p>
      )}
    </div>
  );
}

/**
 * Una cosa que vence, adentro de su día.
 *
 * El icono y el color del borde dicen QUÉ es (venta, reclamo, pendiente) y el
 * fondo dice CÓMO está: rojo lo que ya venció y sigue en juego, gris lo que ya
 * no está en juego, normal el resto.
 *
 * Los dos datos vienen resueltos del servidor —`tipo` y `apagado`—: esta celda
 * no compara ni un código de estado. Antes comparaba `state` contra `renewed` y
 * `cancelled`, que era vocabulario de ventas adentro de un componente genérico y
 * no había forma de sumarle los códigos de otros dos modelos sin acumularlos
 * todos acá.
 */
function CeldaDeVencimiento({
  item,
  fecha,
  hoy,
  menuName,
}: {
  item: ItemDeAgenda;
  /** El día en el que está dibujada. Viene de la grilla, no del registro. */
  fecha: string;
  hoy: string;
  menuName: string | null;
}) {
  const { borde, texto, Icono } = estiloDe(item.tipo);
  // Comparación de texto contra texto (`"2026-08-17" < "2026-08-18"`), que en
  // `YYYY-MM-DD` es exacta y no depende de la zona del navegador.
  const vencido = !item.apagado && fecha < hoy;

  return (
    <div
      className={clsx(
        'rounded border-l-2 px-1.5 py-1 text-xs leading-tight transition-colors hover:bg-surface-raised',
        item.apagado
          ? 'border-l-fg-faint/40 text-fg-faint'
          : vencido
            ? clsx(borde, 'bg-danger-soft/40')
            : borde,
      )}
    >
      <Link
        to={recordPath(item.modelo, item.id, menuName)}
        className="flex items-start gap-1 font-medium hover:text-accent"
        title={`${item.tipo_label} · ${item.titulo}${item.detalle === '' ? '' : ` — ${item.detalle}`}${item.estado_label === '' ? '' : ` (${item.estado_label})`}`}
      >
        <Icono
          className={clsx('mt-0.5 h-3 w-3 shrink-0', item.apagado ? 'text-fg-faint' : texto)}
          aria-hidden="true"
        />
        {/* El tipo también en texto, para lectores de pantalla y para el día que
            alguien mire esto en blanco y negro: un icono solo no es una etiqueta. */}
        <span className="sr-only">{item.tipo_label}: </span>
        <span className="truncate">{item.titulo}</span>
      </Link>
      {item.importe !== null && item.importe !== '' && (
        <span className="block truncate tabular-nums text-fg-muted">
          {formatCurrency(item.importe, { currency: 'ARS', minimumFractionDigits: 2 })}
        </span>
      )}
      {item.detalle !== '' && (
        <span className="block truncate text-2xs text-fg-faint">{item.detalle}</span>
      )}
    </div>
  );
}
